import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { ApprovalsService } from 'api-clients/monolith';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import messages from './messages';
import { useStyles } from './styles';
import { ApprovalRule } from './types';

export interface DeleteRuleDialogProps {
  rule: ApprovalRule;
  /** How many in-flight approvals this delete would cancel (AXHE-4018). */
  pendingRequests: number;
  /** True while that count is still being fetched. */
  pendingLoading: boolean;
  onCancel: () => void;
  onDeleted: () => void;
}

type DeleteResponse = Awaited<
  ReturnType<typeof ApprovalsService.deleteInternalApiApprovalsRules>
>;

/**
 * Confirmation for deleting a rule.
 *
 * A centred dialog rather than the editor's drawer: this is a single, reversible-
 * by-nobody decision with no data entry, which is exactly what a modal is for.
 *
 * It names two consequences, not one. The cascade — the rule *and* its levels —
 * comes from `level_count`, so a collapsed row that never loaded its tree still
 * states the real number. The second is what the design's copy left out and
 * AXHE-4018 answered: deleting a rule cancels the approvals it was gating, so
 * offers sitting in someone's queue stop being held by it. An admin who deletes
 * a rule without being told that is discovering it from the affected candidates.
 */
export const DeleteRuleDialog: React.FC<DeleteRuleDialogProps> = ({
  rule,
  pendingRequests,
  pendingLoading,
  onCancel,
  onDeleted,
}) => {
  const styles = useStyles();

  const destroy = useCallback(
    () => ApprovalsService.deleteInternalApiApprovalsRules(rule.id as string),
    [rule.id],
  );
  const options = useMemo(() => ({ onSuccess: onDeleted }), [onDeleted]);
  const { result, mutation } = useApiServiceMutation<DeleteResponse, typeof destroy>(
    destroy,
    options,
  );

  return (
    <Dialog open onClose={onCancel} aria-labelledby="delete-rule-title">
      <DialogTitle id="delete-rule-title">
        <FormattedMessage {...messages.deleteRuleTitle} values={{ name: rule.name }} />
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1">
          <FormattedMessage
            {...messages.deleteRuleCascade}
            values={{ count: rule.level_count ?? rule.levels?.length ?? 0 }}
          />
        </Typography>

        {/* Only when there are any. "0 approvals will be cancelled" is noise
            that trains people to skip the line that matters. */}
        {pendingRequests > 0 && (
          <Typography variant="body1" className={styles.dialogWarning}>
            <FormattedMessage
              {...messages.deleteRulePending}
              values={{ count: pendingRequests }}
            />
          </Typography>
        )}

        {/* One message for every failure, deliberately. The one status worth
            distinguishing is the 409 from `reject_legacy_managed!`, and that is
            unreachable from here: `RuleRow` renders no delete action for a
            legacy-managed rule. If that ever drifts, this is the line that has
            to grow a status-specific branch — the server's "managed on the
            Opening Approvals page" reason is more useful than "try again". */}
        {result.status === 'error' && (
          <Typography variant="body1" className={styles.dialogError} role="alert">
            <FormattedMessage {...messages.deleteFailed} />
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>
          <FormattedMessage {...globalMessages.cancel} />
        </Button>
        {/* Labelled with the action, not "Yes" — and held until the pending
            count has actually loaded, so nobody confirms a cascade whose size
            the dialog has not yet told them. */}
        <Button
          className={styles.dangerButton}
          onClick={() => mutation()}
          disabled={pendingLoading || result.status === 'loading'}
        >
          <FormattedMessage {...messages.deleteRule} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
